/**
 * Human-readable CAR activity labels and summaries (no database imports).
 */

import type { CorrectiveActionStatus } from "./quality-corrective-action-helpers.js";

export interface CorrectiveActionActivitySummaryInput {
  actionType: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  note: string | null;
}

const FIELD_LABELS: Record<string, string> = {
  title: "Title",
  problemDescription: "Problem description",
  immediateContainment: "Immediate containment",
  rootCauseAnalysis: "Root cause analysis",
  correctiveActionPlan: "Corrective action plan",
  implementationNotes: "Implementation notes",
  effectivenessVerification: "Effectiveness verification",
  continuedReviewOn: "Scheduled date for continued review",
  preventiveAction: "Preventive action",
  ownerUserId: "Owner",
  sourceType: "Source",
  targetCloseDate: "Target close date",
  status: "Status",
  clientFsAccountId: "Customer",
  salesOrderId: "Sales order",
  qualityIncidentId: "QC incident",
  qualityCaseId: "Quality case",
  serialFciId: "Serial FCI",
  deviceId: "Device",
};

function activityStatusLabel(status: string | null | undefined): string {
  if (status == null || status.trim() === "") return "None";
  switch (status.trim().toLowerCase() as CorrectiveActionStatus) {
    case "draft":
      return "Draft";
    case "open":
      return "Open";
    case "containment":
      return "Containment";
    case "analysis":
      return "Root cause analysis";
    case "action":
      return "Corrective action";
    case "verification":
      return "Verification";
    case "closed":
      return "Closed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

export function correctiveActionFieldLabel(fieldName: string): string {
  return FIELD_LABELS[fieldName] ?? fieldName;
}

export function formatCorrectiveActionFieldList(labels: string[]): string {
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

export function summarizeCorrectiveActionActivity(activity: CorrectiveActionActivitySummaryInput): string {
  if (activity.actionType === "comment") {
    return activity.note?.trim() || "Comment";
  }
  if (activity.actionType === "created") {
    return activity.note?.trim() || "Created";
  }
  if (activity.actionType === "status_changed" && activity.fieldName === "status") {
    if (activity.note != null && activity.note.trim() !== "") {
      return activity.note.trim();
    }
    const oldLabel = activityStatusLabel(activity.oldValue);
    const newLabel = activityStatusLabel(activity.newValue);
    return `Status ${oldLabel} → ${newLabel}`;
  }
  if (activity.actionType === "fields_updated" && activity.fieldName == null) {
    const fields = activity.note?.trim();
    return fields ? `Updated ${fields}` : "Updated multiple fields";
  }
  if (
    (activity.actionType === "field_updated" ||
      activity.actionType === "fields_updated" ||
      activity.actionType === "updated") &&
    activity.fieldName != null
  ) {
    return `Updated ${correctiveActionFieldLabel(activity.fieldName)}`;
  }
  if (activity.fieldName != null) {
    return `Updated ${correctiveActionFieldLabel(activity.fieldName)}`;
  }
  return activity.note?.trim() || activity.actionType;
}

/** Text stored on a field change. Comments already render their note separately. */
export function correctiveActionActivityChangeText(activity: CorrectiveActionActivitySummaryInput): string | null {
  if (activity.actionType === "comment" || activity.actionType === "created" || activity.actionType === "status_changed") {
    return null;
  }
  const next = activity.newValue?.trim() ?? "";
  if (next === "") return null;
  return activity.newValue;
}
